import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getNewsDetails } from '../actions';
import { NewsItem } from '../../../../interfaces/api/congresses';
import { openLink } from '../../../../shared/utils/helper';

const NewsDetails = () => {
    const { id } = useParams<{ id: string }>();
    const [newsItem, setNewsItem] = useState<NewsItem | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchDetails = async () => {
            if (!id) {
                setError('News ID is missing.');
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            const data = await getNewsDetails(Number(id));
            if (data) {
                setNewsItem(data);
            } else {
                setError('News item not found or failed to fetch.');
            }
            setIsLoading(false);
        };
        fetchDetails();
    }, [id]);

    if (isLoading) {
        return (
            <div className="container mx-auto py-4">
                <div className="animate-pulse">
                    <div className="bg-gray-200 dark:bg-gray-700 h-10 w-full mb-4 rounded"></div>
                    <div className="bg-gray-200 dark:bg-gray-700 h-6 w-1/4 mb-6 rounded"></div>
                    
                    <div className="bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
                            {[...Array(6)].map((_, index) => (
                                <div key={index} className="flex flex-col">
                                    <div className="bg-gray-200 dark:bg-gray-700 h-5 w-1/3 mb-2 rounded"></div>
                                    <div className="bg-gray-200 dark:bg-gray-700 h-6 w-2/3 rounded"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <div className="bg-gray-200 dark:bg-gray-700 h-8 w-1/3 mb-4 rounded"></div>
                        <div className="space-y-3">
                            <div className="bg-gray-200 dark:bg-gray-700 h-4 w-full rounded"></div>
                            <div className="bg-gray-200 dark:bg-gray-700 h-4 w-5/6 rounded"></div>
                            <div className="bg-gray-200 dark:bg-gray-700 h-4 w-4/6 rounded"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto py-4 text-center text-red-500">
                <p>{error}</p>
            </div>
        );
    }

    if (!newsItem) {
        return (
            <div className="container mx-auto py-4 text-center text-gray-500">
                <p>No news details available.</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-4">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 break-words overflow-hidden">{newsItem.title}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Published: {new Date(newsItem.publishdate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })}
            </p>

            <div className="bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
                    {newsItem.url && (
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Link / Reference:</span>
                            <a onClick={() => openLink(newsItem.url || '')} className="text-blue-600 hover:underline dark:text-blue-400 cursor-pointer">
                                {newsItem.url}
                            </a>
                        </div>
                    )}
                    {newsItem.publisher && (
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Publisher:</span>
                            <span className="text-gray-900 dark:text-white">{newsItem.publisher}</span>
                        </div>
                    )}
                    {newsItem.readabilityscore && (
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Readability score:</span>
                            <span className="text-gray-900 dark:text-white">{newsItem.readabilityscore}</span>
                        </div>
                    )}
                    {newsItem.sentiment && (
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Sensation:</span>
                            <span className="text-gray-900 dark:text-white">{newsItem.sentiment}</span>
                        </div>
                    )}
                    {newsItem.accesslevel && (
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Access Level:</span>
                            <span className="text-gray-900 dark:text-white">{newsItem.accesslevel}</span>
                        </div>
                    )}
                    {newsItem.publishdate && (
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Date:</span>
                            <span className="text-gray-900 dark:text-white">{new Date(newsItem.publishdate).getFullYear()}</span>
                        </div>
                    )}
                     {newsItem.type_of_study_value && (
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Type of the study:</span>
                            <span className="text-gray-900 dark:text-white">{newsItem.type_of_study_value}</span>
                        </div>
                    )}
                </div>
            </div>

            {newsItem.description && (
                <div className="bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Description</h2>
                    <div 
                        className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
                        dangerouslySetInnerHTML={{ __html: newsItem.description }}
                    />
                </div>
            )}
        </div>
    );
};

export default NewsDetails;